/**
 * Message Sync Manager
 * 管理离线消息同步、增量拉取等
 */

import {
  // getChatMessages,
  getConversationList,
} from '@/api/chat'
import { dbManager } from '@/indexdb/chat'
import { groupBy } from 'lodash'
import { formatConversations } from '@/utils'
import { IMContentDecrypt } from '@/utils/crypto'
import { PRIVATE_MSG_TYPES } from '@/common/constants/msgTypes'

class MessageSyncManager {
  // 私有字段
  #isSyncing = false

  /**
   * 同步离线消息（循环拉取直到 is_over = true）✅
   * @returns {Promise<void>}
   */
  // async syncOfflineMessages() {
  //   if (this.#isSyncing) {
  //     console.warn('[MessageSync] Already syncing')
  //     return
  //   }

  //   // 每次都要重新获取数据库实例
  //   const db = dbManager.getDatabase()
  //   if (!db) throw new Error('Database not initialized')

  //   this.#isSyncing = true
  //   console.log('[MessageSync] Starting offline message sync...')

  //   try {
  //     // 获取同步游标
  //     /**
  //      * 为什么要单独维护一个游标，而不是直接从 messages 表里获取最大的 seq_id？
  //      * 1.
  //      * 场景：用户删除了本地消息
  //      * 删除前：本地最大 seq_id = 100
  //      * 删除后：用户删除了 seq_id=90-100 的消息，本地最大 seq_id = 89
  //      * 下次同步会重新拉取 seq_id=89-100 的消息
  //      * 问题：重复拉取已同步过的消息 ❌
  //      * 2. 还有性能问题
  //      */
  //     const cursor = await db.sync_cursor.get('cursor')
  //     let lastSeqId = cursor?.lastSeqId ?? 0

  //     let isOver = false

  //     // 循环拉取所有离线消息
  //     while (!isOver) {
  //       console.log(`[MessageSync] Fetching messages after seq_id: ${lastSeqId}`)

  //       const response = await getChatMessages({
  //         after_seq_id: lastSeqId,
  //         size: 20
  //       })

  //       const { list = [], is_over = false } = response

  //       if (list.length > 0) {
  //         console.log(`[MessageSync] Received ${list.length} messages`)

  //         // 使用通用的批量保存方法
  //         await this.saveMessagesBatch(list, {
  //           shouldFetchMissingConversations: true, // 需要获取缺失的会话
  //           shouldUpdateCursor: true // 需要更新游标
  //         })

  //         // 更新游标变量（用于下一轮）
  //         const maxSeqId = Math.max(...list.map(m => m.seq_id || 0))
  //         if (maxSeqId > lastSeqId) {
  //           lastSeqId = maxSeqId
  //         }
  //       }

  //       isOver = is_over
  //     }

  //   } catch (error) {
  //     console.error('[MessageSync] Failed to sync offline messages:', error)
  //     throw error
  //   } finally {
  //     this.#isSyncing = false
  //   }
  // }

  /**
   * 批量保存消息到数据库（通用方法）✅
   * @param {Array} messages - 消息列表
   * @param {Object} options - 配置选项
   * @param {boolean} [options.shouldFetchMissingConversations=true] - 是否获取缺失的会话
   * @param {boolean} [options.shouldUpdateCursor=false] - 是否更新同步游标
   * @returns {Promise<{
   *   savedMessagesCount: number,
   *   updatedConversationsCount: number,
   *   conversationIds: Array<string>
   * }>}
   */
  async saveMessagesBatch(messages, options = {}) {
    const db = dbManager.getDatabase()
    if (!db) throw new Error('Database not initialized')
    const {
      shouldFetchMissingConversations = true,
      // shouldUpdateCursor = true,
    } = options

    if (messages.length === 0) {
      return {
        conversationIds: []
      }
    }

    console.log(`[MessageSync] Saving ${messages.length} messages (fetchMissingConversations: ${shouldFetchMissingConversations})`)

    // ✅ 步骤1: 分类会话（查询本地DB）
    const {
      conversationGroup,
      existingConversations,
      missingConversationIds
    } = await this.#classifyConversations(db, messages)

    // ✅ 步骤2: 预加载缺失的会话（调用API，在事务外）
    let fetchedConversations = []
    if (shouldFetchMissingConversations && missingConversationIds.length > 0) {
      fetchedConversations = await this.#prefetchMissingConversations(missingConversationIds)
      fetchedConversations = formatConversations(fetchedConversations, v => ({
        ...v,
        unreadCount: (v.unreadCount || 0) + 1
      }))
    }

    // ✅ 步骤3: 计算最大 seq_id（如果需要更新游标）
    // let maxSeqId = 0
    // if (shouldUpdateCursor) {
    //   maxSeqId = Math.max(...messages.map(m => m.seq_id || 0))
    // }

    // ✅ 步骤4: 使用事务保证原子性
    // const tables = shouldUpdateCursor
    //   ? [db.messages, db.conversations, db.sync_cursor]
    //   : [db.messages, db.conversations]

    await db.transaction('rw', [db.messages, db.conversations], async () => {
      // 4.1 保存消息到 DB
      await db.messages.bulkPut(messages)
      console.log(`[MessageSync] Saved ${messages.length} messages`)

      // 4.2 保存预加载的会话（缺失的会话）
      if (fetchedConversations.length > 0) {
        await db.conversations.bulkPut(fetchedConversations)
        console.log(`[MessageSync] Saved ${fetchedConversations.length} new conversations`)
      }

      // 4.3 更新已存在的会话
      for (const [convId, existingConv] of existingConversations) {
        const conversationMessages = conversationGroup[convId]
        await this.#updateExistingConversation(db, existingConv, conversationMessages)
      }

      // 4.4 更新游标（如果需要）
      // if (shouldUpdateCursor && maxSeqId > 0) {
      //   const cursor = await db.sync_cursor.get('cursor')
      //   const lastSeqId = cursor?.lastSeqId ?? 0

      //   if (maxSeqId > lastSeqId) {
      //     await db.sync_cursor.update('cursor', {
      //       lastSeqId: maxSeqId,
      //       lastSyncTime: Date.now()
      //     })
      //     console.log(`[MessageSync] Updated cursor to seq_id: ${maxSeqId}`)
      //   }
      // }
    })

    console.log(`[MessageSync] Transaction committed successfully`)

    // 返回结果数据
    return {
      conversationIds: Object.keys(conversationGroup)
    }
  }

  /**
   * 对消息列表进行会话分组，并区分已存在和不存在的会话 ✅
   * @private
   * @param {Object} db - 数据库实例
   * @param {Array} messages - 消息列表
   * @returns {Promise<{
   *   conversationGroup: Object,
   *   existingConversations: Map<string, Object>,
   *   missingConversationIds: Array<string>
   * }>}
   */
  async #classifyConversations(db, messages) {

    // 步骤1: 按会话ID分组
    const conversationGroup = groupBy(messages, 'conversation_id')

    // 步骤2: 查询哪些会话已存在
    const existingConversations = new Map()
    const missingConversationIds = []

    for (const convId in conversationGroup) {
      const existingConv = await db.conversations.get(convId)

      if (existingConv) {
        existingConversations.set(convId, existingConv)
      } else {
        missingConversationIds.push(convId)
      }
    }

    console.log(`[MessageSync] Classified conversations: ${existingConversations.size} existing, ${missingConversationIds.length} missing`)

    return {
      conversationGroup,           // 消息按会话分组
      existingConversations,       // 已存在的会话 Map
      missingConversationIds       // 缺失的会话ID列表
    }
  }

  /**
   * 批量获取缺失的会话信息（在事务外部调用）✅
   * @private
   * @param {Object} db - 数据库实例
   * @param {Array<string>} conversationIds - 缺失的会话ID列表
   * @returns {Promise<Array>} 会话列表
   */
  async #prefetchMissingConversations(conversationIds) {
    if (conversationIds.length === 0) {
      return []
    }

    try {
      const response = await getConversationList({
        conversation_ids: conversationIds
      })
      const { list = [] } = response
      return list
    } catch (error) {
      console.error('[MessageSync] Failed to fetch missing conversations:', error)
      return []
    }
  }

  /**
   * 更新已存在的会话 ✅
   * @private
   * @param {Object} conversation - 会话对象
   * @param {Array} messages - 该会话的消息列表
   */
  async #updateExistingConversation(db, conversation, messages) {
    if (messages.length === 0) return
    // 降序排列：最新消息在前
    const sortMessages = messages.toSorted((a, b) => b.created_at - a.created_at)
    const readAt = conversation.target_read_at || 0
    const lastMsgAt = conversation.last_msg_at || 0
    const updates = {
      unreadCount: conversation.unreadCount || 0,
    }
    // 从新到旧遍历
    sortMessages.forEach(msg => {
      switch(msg.sub_type) {
        case PRIVATE_MSG_TYPES.HINT:
        case PRIVATE_MSG_TYPES.HINT_DOUBLE:
          // 只更新未读数和 last_msg_at
          if (msg.created_at > readAt) {
            updates.unreadCount++
          }
          if (!updates.last_msg_at && msg.created_at > lastMsgAt) {
            updates.last_msg_at = msg.created_at
          }
          break;
        case PRIVATE_MSG_TYPES.CONTROL_COMMAND_UPDATE:
          // 什么也不做，不更新未读数，也不更新 last_msg_at，更不更新 preview
          break;
        default:
          if (msg.created_at > readAt) {
            updates.unreadCount++
          }
          if (msg.created_at > lastMsgAt) {
            // preview 更新为 最后的一条非前面三种消息，没有就不更新
            if (!updates.preview) {
              updates.preview = IMContentDecrypt(msg.msg_preview)
            }
            if (!updates.last_msg_at) {
              updates.last_msg_at = msg.created_at
            }
          }
      }
    })

    // ✅ 使用传入的 db 实例进行更新（在事务中）
    await db.conversations.update(conversation.conversation_id, updates)
  }
}

// 导出单例
export const messageSyncManager = new MessageSyncManager()

export default MessageSyncManager
